import React, { useState, useEffect, useRef } from 'react';

const SimpleRichTextEditor = ({ value, onChange, placeholder, className, error }) => {
  const editorRef = useRef(null);
  const [focus, setFocus] = useState(false);
  const [content, setContent] = useState(value || '');

  // Format buttons handlers
  const applyFormat = (command, value = null) => {
    try {
      // Make sure the editor has focus before applying the command
      if (editorRef.current) {
        editorRef.current.focus();
        
        // Save selection state
        const selection = window.getSelection();
        const hasSelection = selection.rangeCount > 0;
        const range = hasSelection ? selection.getRangeAt(0) : null;
        
        // Apply the command
        document.execCommand(command, false, value);
        
        // Call handleChange after a small delay to ensure content is updated
        setTimeout(() => {
          // Restore selection if it was lost
          if (range && selection.rangeCount === 0) {
            selection.addRange(range);
          }
          handleChange();
        }, 10);
      }
    } catch (error) {
      console.error(`Error applying format "${command}":`, error);
    }
  };

  const handleBold = () => applyFormat('bold');
  const handleItalic = () => applyFormat('italic');
  const handleUnderline = () => applyFormat('underline');
  const handleBulletList = () => {
    // Force focus on editor before applying bullet list
    if (editorRef.current) {
      editorRef.current.focus();
      
      try {
        // Save selection state before applying command
        const selection = window.getSelection();
        if (selection.rangeCount === 0) {
          // If no selection, place cursor at end
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        const savedRange = selection.getRangeAt(0).cloneRange();
        
        // Make sure we're in a proper editable context
        if (!isInEditableContent(savedRange.startContainer)) {
          // Move to a valid position in the editor
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        // Apply the command with a small delay for cross-browser compatibility
        setTimeout(() => {
          // Restore selection if needed
          if (selection.rangeCount === 0) {
            selection.addRange(savedRange);
          }
          
          // Try to detect if we're already in a list to toggle it
          const isInList = !!getClosestElementByTag(selection.anchorNode, ['UL']);
          
          // Apply the command - toggle if already in list
          document.execCommand('insertUnorderedList', false, null);
          
          // Some browsers need help with proper list creation when starting with empty content
          if (!isInList && editorRef.current.innerHTML === '') {
            editorRef.current.innerHTML = '<ul><li><br></li></ul>';
            const range = document.createRange();
            const li = editorRef.current.querySelector('li');
            if (li) {
              range.setStart(li, 0);
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
          
          // Ensure content is updated
          handleChange();
        }, 10);
      } catch (e) {
        console.error("Error applying bullet list:", e);
        // Fallback direct application
        document.execCommand('insertUnorderedList', false, null);
        handleChange();
      }
    }
  };
  
  // Helper to check if we're in editable content
  const isInEditableContent = (node) => {
    while (node) {
      if (node === editorRef.current) return true;
      node = node.parentNode;
    }
    return false;
  };
  const handleNumberedList = () => {
    // Force focus on editor before applying numbered list
    if (editorRef.current) {
      editorRef.current.focus();
      
      // Save selection state before applying command
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      // Apply numbered list with a small delay for cross-browser compatibility
      setTimeout(() => {
        // Restore selection if needed
        if (selection.rangeCount === 0) {
          selection.addRange(range);
        }
        
        // Apply the command
        document.execCommand('insertOrderedList', false, null);
        
        // Ensure content is updated
        handleChange();
      }, 10);
    }
  };
  const handleClear = () => {
    if (!editorRef.current) return;
    
    try {
      // Simply clear all content from the editor
      editorRef.current.innerHTML = '';
      
      // Make sure to focus the editor after clearing
      editorRef.current.focus();
      
      // Notify about the change
      handleChange();
    } catch (e) {
      console.error("Error clearing content:", e);
    }
  };
  
  // Helper function to find closest parent element with specified tag
  const getClosestElementByTag = (node, tags) => {
    while (node && node !== editorRef.current) {
      if (node.nodeType === 1 && tags.includes(node.tagName.toUpperCase())) {
        return node;
      }
      node = node.parentNode;
    }
    return null;
  };
  
  // Helper function to check if node has formatted parents
  const hasFormattedParents = (node) => {
    const formattedTags = ['B', 'STRONG', 'I', 'EM', 'U', 'STRIKE', 'SUP', 'SUB', 'FONT', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI'];
    return !!getClosestElementByTag(node, formattedTags);
  };
  const handleClearFormatting = () => {
    if (!editorRef.current) return;
    
    // Force focus
    editorRef.current.focus();
    
    // Get the current selection
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    // Clone the range to preserve it
    const originalRange = selection.getRangeAt(0).cloneRange();
    
    try {
      // Method 1: Use multiple format removal commands
      document.execCommand('removeFormat', false, null);
      
      // Method 2: Special handling for lists - a common issue with clear formatting
      const isInsideList = !!getClosestElementByTag(originalRange.commonAncestorContainer, ['UL', 'OL', 'LI']);
      
      if (isInsideList) {
        // Try to exit the list
        document.execCommand('outdent', false, null);
        
        // If still in a list, try toggling the list off
        if (getClosestElementByTag(originalRange.commonAncestorContainer, ['UL', 'OL', 'LI'])) {
          document.execCommand('insertOrderedList', false, null);
          document.execCommand('insertUnorderedList', false, null);
        }
      }
      
      // Method 3: Replace with plain text version (most reliable but can lose selection)
      const selectedText = selection.toString();
      if (selectedText && selectedText.trim().length > 0) {
        document.execCommand('insertText', false, selectedText);
      }
      
      handleChange();
    } catch (e) {
      console.error("Error clearing formatting:", e);
    }
  };

  const handleAlign = (align) => applyFormat('justify' + align);

  // Handle content changes
  const handleChange = () => {
    if (!editorRef.current) return;
    
    // Get the current content
    const newContent = editorRef.current.innerHTML;
    
    // Update internal state if changed
    if (newContent !== content) {
      setContent(newContent);
      
      // Notify parent component of the change
      if (onChange) {
        onChange(newContent);
      }
    }
  };

  // Preserve cursor position when buttons are clicked
  const handleButtonMouseDown = (e) => {
    e.preventDefault(); // Prevents the editor from losing focus
  };

  // Initialize with value if provided
  useEffect(() => {
    if (editorRef.current) {
      // Handle null/undefined/empty values
      const newValue = value !== undefined && value !== null ? value : '';
      
      // Only update if the content has actually changed to avoid cursor reset
      if (editorRef.current.innerHTML !== newValue) {
        editorRef.current.innerHTML = newValue;
        setContent(newValue);
      }
    }
  }, [value]);

  return (
    <div className={`simple-rich-text-editor ${className || ''} ${error ? 'has-error' : ''}`}>
      <div className="editor-toolbar">
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={handleBold} 
          className="toolbar-btn"
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={handleItalic} 
          className="toolbar-btn"
          title="Italic"
        >
          <em>I</em>
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={handleUnderline} 
          className="toolbar-btn"
          title="Underline"
        >
          <u>U</u>
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={handleBulletList} 
          className="toolbar-btn"
          title="Bullet List"
        >
          • List
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={handleNumberedList} 
          className="toolbar-btn"
          title="Numbered List"
        >
          1. List
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={() => handleAlign('Left')} 
          className="toolbar-btn"
          title="Align Left"
        >
          ←
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={() => handleAlign('Center')} 
          className="toolbar-btn"
          title="Align Center"
        >
          ↔
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={() => handleAlign('Right')} 
          className="toolbar-btn"
          title="Align Right"
        >
          →
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={handleClearFormatting} 
          className="toolbar-btn"
          title="Remove Formatting"
        >
          Clear Format
        </button>
        <button 
          type="button" 
          onMouseDown={handleButtonMouseDown} 
          onClick={handleClear} 
          className="toolbar-btn"
          title="Clear All Content"
        >
          Clear All
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="editor-content"
        onInput={handleChange}
        onBlur={() => setFocus(false)}
        onFocus={() => setFocus(true)}
        data-placeholder={!content && !focus ? placeholder : ''}
        dangerouslySetInnerHTML={value && !content ? { __html: value } : undefined}
      />
      {error && <div className="error-message">{error.message}</div>}
    </div>
  );
};

export default SimpleRichTextEditor;
